import { Plus, Trash2, AlertCircle } from "lucide-react";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };

export default function AvailabilityEditor({ value, onChange, testidPrefix = "availability" }) {
  const av = value || { weekly_pattern: {}, time_off: [], max_hours_per_week: null, notes: "" };

  const setWeekly = (dayKey, windows) => {
    onChange({
      ...av,
      weekly_pattern: { ...(av.weekly_pattern || {}), [dayKey]: windows },
    });
  };

  const addWindow = (dayKey) => {
    const cur = (av.weekly_pattern || {})[dayKey] || [];
    setWeekly(dayKey, [...cur, { start: "09:00", end: "17:00" }]);
  };

  const updateWindow = (dayKey, idx, patch) => {
    const cur = ((av.weekly_pattern || {})[dayKey] || []).slice();
    cur[idx] = { ...cur[idx], ...patch };
    setWeekly(dayKey, cur);
  };

  const removeWindow = (dayKey, idx) => {
    const cur = ((av.weekly_pattern || {})[dayKey] || []).filter((_, i) => i !== idx);
    setWeekly(dayKey, cur);
  };

  const addTimeOff = () => {
    onChange({
      ...av,
      time_off: [
        ...(av.time_off || []),
        {
          id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          start_date: "",
          end_date: "",
          reason: "",
        },
      ],
    });
  };

  const updateTimeOff = (idx, patch) => {
    const cur = (av.time_off || []).slice();
    cur[idx] = { ...cur[idx], ...patch };
    onChange({ ...av, time_off: cur });
  };

  const removeTimeOff = (idx) => {
    onChange({
      ...av,
      time_off: (av.time_off || []).filter((_, i) => i !== idx),
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5" data-testid={`${testidPrefix}-weekly`}>
        {DAY_KEYS.map((k) => {
          const windows = (av.weekly_pattern || {})[k] || [];
          return (
            <div
              key={k}
              className="grid grid-cols-12 gap-2 items-start"
              data-testid={`${testidPrefix}-day-${k}`}
            >
              <div className="col-span-3 text-sm text-clinical-text pt-1.5">
                {DAY_LABELS[k]}
              </div>
              <div className="col-span-9 space-y-1.5">
                {windows.length === 0 ? (
                  <div className="text-[11px] text-clinical-subtext italic">
                    Not available
                  </div>
                ) : (
                  windows.map((w, idx) => (
                    <div key={`${k}-${idx}-${w.start || ""}-${w.end || ""}`} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={w.start || ""}
                        onChange={(e) => updateWindow(k, idx, { start: e.target.value })}
                        className="input-clinical max-w-[110px]"
                        data-testid={`${testidPrefix}-${k}-start-${idx}`}
                      />
                      <span className="text-clinical-subtext text-xs">to</span>
                      <input
                        type="time"
                        value={w.end || ""}
                        onChange={(e) => updateWindow(k, idx, { end: e.target.value })}
                        className="input-clinical max-w-[110px]"
                        data-testid={`${testidPrefix}-${k}-end-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeWindow(k, idx)}
                        className="btn-ghost !p-1.5 hover:!text-destructive"
                        data-testid={`${testidPrefix}-${k}-remove-${idx}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))
                )}
                <button
                  type="button"
                  onClick={() => addWindow(k)}
                  className="text-[11px] text-clinical-primary hover:underline inline-flex items-center gap-1"
                  data-testid={`${testidPrefix}-${k}-add`}
                >
                  <Plus className="h-3 w-3" /> Add hours
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-clinical-border pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" /> Time off
          </div>
          <button
            type="button"
            onClick={addTimeOff}
            className="text-[11px] text-clinical-primary hover:underline inline-flex items-center gap-1"
            data-testid={`${testidPrefix}-add-time-off`}
          >
            <Plus className="h-3 w-3" /> Add time-off
          </button>
        </div>
        {(av.time_off || []).length === 0 ? (
          <div className="text-[11px] text-clinical-subtext italic">
            No upcoming time-off recorded.
          </div>
        ) : (
          <ul className="space-y-1.5" data-testid={`${testidPrefix}-time-off-list`}>
            {(av.time_off || []).map((t, idx) => (
              <li
                key={t.id || idx}
                className="grid grid-cols-12 gap-2 items-center"
                data-testid={`${testidPrefix}-time-off-${idx}`}
              >
                <input
                  type="date"
                  value={t.start_date || ""}
                  onChange={(e) => updateTimeOff(idx, { start_date: e.target.value })}
                  className="input-clinical col-span-4"
                  data-testid={`${testidPrefix}-time-off-start-${idx}`}
                />
                <input
                  type="date"
                  value={t.end_date || ""}
                  onChange={(e) => updateTimeOff(idx, { end_date: e.target.value })}
                  className="input-clinical col-span-4"
                  data-testid={`${testidPrefix}-time-off-end-${idx}`}
                />
                <input
                  value={t.reason || ""}
                  onChange={(e) => updateTimeOff(idx, { reason: e.target.value })}
                  placeholder="Reason (optional)"
                  className="input-clinical col-span-3"
                  data-testid={`${testidPrefix}-time-off-reason-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeTimeOff(idx)}
                  className="btn-ghost !p-1.5 hover:!text-destructive col-span-1"
                  data-testid={`${testidPrefix}-time-off-remove-${idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
